import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { StatistikKunder } from '../../models/StatistikKunder';
import { IndexCard } from '../../../auth/store/models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from '../../../core/alerts/alert.service';
import { Router } from '@angular/router';
import { StatistikModalSagContainer } from '../statistik-kunder-sag-modal/statistik-modal-sag.container';
import { ExcelService } from '@store';
import { StatistikModalPlantidContainer } from '../statistik-kunder-plantid-modal/statistik-modal-plantid.container';
import { saveAs } from 'file-saver';
import { firstDayLastMonth } from '../../../shared/month-picker/month-picker.helper';
import { StatistikKunderPlanNorm } from '../../models/StatistikKunderPlanNorm';
import { StatistikService } from '../../statistik.service';

@Component({
    selector: 'app-statistik-kunder-ui',
    templateUrl: './statistik-kunder.component.html',
    styleUrls: ['./statistik-kunder.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class StatistikKunderComponent implements OnChanges, OnInit {
    @Input() kunder: StatistikKunder[];
    @Input() medarbejdere: IndexCard[];
    @Input() laCards: IndexCard[];
    @Input() kaCards: IndexCard[];
    @Input() periode: string;
    @Input() userId: number;
    @Input() planNorm: StatistikKunderPlanNorm | null;
    @Output() selectUser = new EventEmitter<number>();
    @Output() periodeChange = new EventEmitter<string>();
    @Output() exportExcel = new EventEmitter<any>();
    @Output() reload = new EventEmitter();
    decimal: number;
    hourFormat = 'Timer';
    inDeciminuts: boolean;
    removeAfsluttet: boolean;
    filteredStatistikKunder: StatistikKunder[];
    selectedMedarbejderId: number;
    selectedLAId: number;
    selectedKAId: number;
    selecetKoncern: string | undefined;
    koncernList: (string | undefined)[] = [];
    months: string[] = [
        'Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'December'
    ];
    monthIndexArray = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    monthArray = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    monthOrder: number[] = [];
    monthOrder2: number[] = [];

    constructor(
        private modalService: NgbModal,
        private alertService: AlertService,
        private router: Router,
        private excel: ExcelService,
        private statistikService: StatistikService,
    ) {
        this.decimal = 2;
    }

    ngOnInit(): void {
        this.selectedMedarbejderId = this.userId;
        this.selectUser.emit(this.userId);
        this.onPeriodeChange(firstDayLastMonth());
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.kunder && this.kunder) {
            this.filterStatistikKunder();

            this.koncernList = [];
            if (this.filteredStatistikKunder && this.filteredStatistikKunder.length > 0) {
                this.filteredStatistikKunder.forEach(kunde => {
                    if (!this.koncernList.includes(kunde.koncern) && kunde.koncern !== null) {
                        this.koncernList.push(kunde.koncern);
                    }
                });
            }
        }
        this.filteredStatistikKunder?.sort((a, b) => a.glkundeid - b.glkundeid);
    }

    onPeriodeChange(periode: string) {
        const startDate = parseInt(periode.split('-')[1], 10);
        const numberOfMonths = 12;
        this.monthOrder = [];
        this.monthOrder2 = [];

        for (let i = 0; i < numberOfMonths; i++) {
            const monthNumber = (startDate + i - 1) % 12;
            this.monthOrder.push(monthNumber);
        }

        for (let i = 0; i < numberOfMonths; i++) {
            const monthNumber = (startDate + i - 1) % 12 + 1;
            this.monthOrder2.push(monthNumber);
        }

        this.filteredStatistikKunder = [];
        this.kunder = [];
        this.periodeChange.emit(periode);
    }

    getTimeBudget(kunde: StatistikKunder, month: number, hourFormat: string): number {
        if (kunde.timeBudgets) {
            return kunde.timeBudgets
                .filter(budget => budget.mth === month)
                .reduce((total, budget) =>
                    total + (budget.timer * (hourFormat === 'Timer' ? 1 : budget.timesats)), 0);
        }

        return 0;
    }

    getTotalTimeBudget(kunde: StatistikKunder, hourFormat: string): number {
        if (kunde.timeBudgets) {
                return kunde.timeBudgets
                    .reduce((total, budget) =>
                        total + (budget.timer * (hourFormat === 'Timer' ? 1 : budget.timesats)), 0);
        }

        return 0;
    }

    getTimeRegistrations(kunde: StatistikKunder, month: number, hourFormat: string) {
        if (kunde.timeRegistrations) {
            return kunde.timeRegistrations
                .filter(registration => {
                    const dato = new Date(registration.dato);
                    return dato.getMonth() === month;
                })
                .reduce((total, registration) =>
                    total + (registration.timer * (hourFormat === 'Timer' ? 1 : registration.timesats)), 0);
        }

        return 0;
    }

    getTotalTimeRegistrations(kunde: StatistikKunder, hourFormat: string) {
        if (kunde.timeRegistrations) {
            return kunde.timeRegistrations
                .reduce((total, registration) =>
                    total + (registration.timer * (hourFormat === 'Timer' ? 1 : registration.timesats)), 0);
        }

        return 0;
    }

    getTotalMontlyTimeBudget(kunder: StatistikKunder[], month: number, hourFormat: string): number {
        let totalHours: number;
        totalHours = 0;

        if (kunder) {
            kunder.forEach((kunde: StatistikKunder) => {
                totalHours = totalHours + this?.getTimeBudget(kunde, month, hourFormat);
            });
        }

        return totalHours;
    }

    getTotalYearlyTimeBudget(kunder: StatistikKunder[], hourFormat: string): number {
        let totalHours: number;
        totalHours = 0;

        if (kunder) {
            for (const month of this.monthArray) {
                kunder.forEach((kunde: StatistikKunder) => {
                        totalHours = totalHours + this?.getTimeBudget(kunde, month, hourFormat);
                });
            }
        }

        return totalHours;
    }

    getTotalMontlyTimeRegistrations(kunder: StatistikKunder[], month: number, hourFormat: string): number {
        let totalHours: number;
        totalHours = 0;

        if (kunder) {
            kunder.forEach((kunde: StatistikKunder) => {
                    totalHours = totalHours + this?.getTimeRegistrations(kunde, month, hourFormat);
            });
        }

        return totalHours;
    }

    getTotalYearlyTimeRegistrations(kunder: StatistikKunder[], hourFormat: string) {
        let totalHours: number;
        totalHours = 0;

        if (kunder) {
            kunder.forEach((kunde: StatistikKunder) => {
                if (this.selectedMedarbejderId === null) {
                    for (const month of this.monthIndexArray) {
                        totalHours = totalHours + this?.getTimeRegistrations(kunde, month, hourFormat);
                    }
                }
            });
        }

        return totalHours;
    }

    getMedarbejderNormTid(month: number): number {
        return this.planNorm?.normtider
            .filter(n => (new Date(n.dato)).getMonth() === month)
            .reduce((total, n) => total + n.timer, 0);
    }

    getMedarbejderTotalTimeBudgets(month: number): number {
        return this.planNorm?.timeBudgets
            .filter(tb => tb.mth === month)
            .reduce((total, n) => total + n.timer, 0);
    }

    resizeNameLength(kunde: StatistikKunder): string {
        const maxChars = 30;
        const myString = kunde.kunde;

        if (myString.length > maxChars) {
            return myString.slice(0, maxChars) + '...';
        }
        return myString;
    }

    onMedarbejderSelected(medID: number) {
        this.selectUser.emit(medID);
        this.selectedMedarbejderId = medID;
        this.filterStatistikKunder();
    }

    onKundeAnsvarligSelected(ka: number) {
        this.selectedKAId = ka;
        this.filterStatistikKunder();
    }

    onLedelseAnsvarligSelected(la: number) {
        this.selectedLAId = la;
        this.filterStatistikKunder();
    }

    onKoncernSelected(koncern: string) {
        this.selecetKoncern = koncern;
        this.filterStatistikKunder();
    }

    filterStatistikKunder() {
        const filteredKunderByKA = !this.selectedKAId ? this.kunder : this.kunder.filter(kunde => kunde.kundeansvarlig === this.selectedKAId);
        const filteredKunderByKAAndLA = !this.selectedLAId ? filteredKunderByKA : filteredKunderByKA.filter(kunde => kunde.ledelsesansvarlig === this.selectedLAId);
        const filteredKunderByKAAndLAAndKoncern = !this.selecetKoncern ? filteredKunderByKAAndLA : filteredKunderByKAAndLA.filter(kunde => kunde.koncern === this.selecetKoncern);

        const kunderFilteredTimebudgets = !this.selectedMedarbejderId ? filteredKunderByKAAndLAAndKoncern : filteredKunderByKAAndLAAndKoncern.map(kunde => {
            const filteredTimeBudgets = kunde.timeBudgets.filter(tbl => tbl.medID === this.selectedMedarbejderId);
            return {...kunde, timeBudgets: filteredTimeBudgets};
        });

        const kunderFilteredTimeRegistrations = !this.selectedMedarbejderId ? filteredKunderByKAAndLAAndKoncern : kunderFilteredTimebudgets.map(kunde => {
            const filteredTimeRegistrations = kunde.timeRegistrations.filter(tr => tr.medId === this.selectedMedarbejderId);
            return {...kunde, timeRegistrations: filteredTimeRegistrations};
        });

        const kunderRemoveZeroTotals = kunderFilteredTimeRegistrations
            .filter(k => this.getTotalTimeBudget(k, 'Timer') !== 0 || this.getTotalTimeRegistrations(k, 'Timer') !== 0);

        const kunderRemoveAfsluttet = !this.removeAfsluttet ? kunderRemoveZeroTotals : kunderRemoveZeroTotals.filter(kunde => [5, 8].includes(kunde.status));

        this.filteredStatistikKunder = kunderRemoveAfsluttet;
    }

    async openSagModal(kunde: StatistikKunder, month: number) {
        const periodeDate = new Date(this.periode);
        let year = periodeDate.getFullYear();
        const startMonth = periodeDate.getMonth();
        if (month < startMonth) {
            year += 1;
        }

        if (this.getTimeBudget(kunde, month, this.hourFormat) > 0) {
            const sagModal = this.modalService.open(StatistikModalSagContainer, {size: 'lg', backdrop: 'static', keyboard: false });
            sagModal.componentInstance.kundeID = kunde.kundeID;
            sagModal.componentInstance.medID = this.selectedMedarbejderId;
            sagModal.componentInstance.month = month;
            sagModal.componentInstance.year = year;
            await sagModal.result.finally(() => this.reload.emit());
            return;
        }
        this.alertService.createAlert('Vælg venligst en plantid som har timer', 'danger');
    }

    async openPlanlagtTimerModal(medarbejderTotalTimeBudgets: number, month: number) {
        const periodeDate = new Date(this.periode);
        let year = periodeDate.getFullYear();
        const startMonth = periodeDate.getMonth();
        if (month < startMonth) {
            year += 1;
        }
        if (medarbejderTotalTimeBudgets > 0) {
            if (this.selectedMedarbejderId !== undefined && this.selectedMedarbejderId !== null) {
                const plantidModal = this.modalService.open(StatistikModalPlantidContainer, {size: 'lg'});
                plantidModal.componentInstance.medID = this.selectedMedarbejderId;
                plantidModal.componentInstance.month = month;
                plantidModal.componentInstance.year = year;
            }
        }
        return;
    }

    onDecimalSelected($event: number) {
        this.decimal = $event;
    }

    onHourFormatSelected($event: string) {
        this.hourFormat = $event;
    }

    onDeciminutsSelected($event: boolean) {
        this.inDeciminuts = $event;
    }

    onRemoveAfsluttetSelected($event: boolean) {
        this.removeAfsluttet = $event;
        this.filterStatistikKunder();
    }

    getMonth(index: number) {
        return this.months[index];
    }

    navigateToClientBasis(id: StatistikKunder) {
        this.router.navigate(['clients', id.kundeID, 'basis']);
        this.statistikService.setVisible(false);
    }

    trackKundeID(_, kunde: StatistikKunder) {
        return kunde.kundeID;
    }

    onExport() {
        const exportArray = [
            ['Kunde', ...this.monthOrder.map(month => [this.months[month], '']).flat(), 'I alt', ''],
            ['', ...Array(13).fill(['P', 'R']).flat()],
            ...this.filteredStatistikKunder.map(kunde => [
                kunde.kunde,
                ...this.monthIndexArray.map(monthIndex => [
                    this.getTimeBudget(kunde, this.monthOrder2[monthIndex], this.hourFormat),
                    this.getTimeRegistrations(kunde, this.monthOrder[monthIndex], this.hourFormat)]).flat(),
                this.getTotalTimeBudget(kunde, this.hourFormat),
                this.getTotalTimeRegistrations(kunde, this.hourFormat),
            ])
        ];
        this.excel.generateExcel(exportArray).subscribe((blob: Blob) => {
            saveAs(blob, 'statistik-kunder.xlsx');
        });
    }
}
